/**
 * Country codes, kept apart from currency codes.
 *
 * ## The confusion this exists to end
 *
 * The Orders resource returns no currency, so the store checks the COUNTRY
 * instead - and the check was written comparing against `'BR'`. Mercado Pago
 * answers `country_code: "BRA"`, ISO 3166-1 **alpha-3**. Three strings that all
 * begin with `BR` were being treated as interchangeable:
 *
 * | `BRL` | ISO 4217 | a CURRENCY. Never a country. |
 * | `BR`  | ISO 3166-1 alpha-2 | a country, in the short form. |
 * | `BRA` | ISO 3166-1 alpha-3 | a country, and what the provider sends. |
 *
 * The cost was not theoretical: a sandbox Pix that Mercado Pago had approved
 * and accredited was refused delivery, because `"BRA" !== "BR"`. The refusal
 * itself was right - an unsatisfiable check must never grant - but the check was
 * comparing a country against the wrong alphabet.
 *
 * ## Normalise, then compare
 *
 * Everything becomes alpha-3 here, once, and the store compares alpha-3 to
 * alpha-3. That is why this is a function rather than `x === 'BR' || x === 'BRA'`
 * scattered about: two spellings of one country invite a third, and the day
 * somebody adds `"Brazil"` to one branch and not another is the day the store
 * delivers to a country it never meant to.
 *
 * ## It fails closed
 *
 * Anything unrecognised answers `null`, which the store treats as UNPROVEN and
 * sends to a person. That includes `BRL`: a currency is not a country, and it
 * is deliberately absent from the table below so it can never be mistaken for
 * one.
 */

/** The only country this store settles money in. ISO 3166-1 alpha-3. */
export const STORE_COUNTRY_CODE = 'BRA';

/** The only currency this store prices in. ISO 4217, and NOT a country. */
export const STORE_CURRENCY_CODE = 'BRL';

/**
 * The countries a Mercado Pago account can belong to, alpha-2 to alpha-3.
 *
 * Deliberately a short, closed table rather than every country there is: these
 * are the sites the provider operates, so they are the values that can actually
 * arrive. Anything outside it is unproven rather than accepted - which is the
 * safe direction, because the store only ever wants one of them.
 */
const ALPHA2_TO_ALPHA3: Readonly<Record<string, string>> = Object.freeze({
  AR: 'ARG',
  BR: 'BRA',
  CL: 'CHL',
  CO: 'COL',
  MX: 'MEX',
  PE: 'PER',
  UY: 'URY',
});

/** The alpha-3 codes this codebase recognises. Derived, so the two cannot drift. */
const KNOWN_ALPHA3: ReadonlySet<string> = new Set(Object.values(ALPHA2_TO_ALPHA3));

/**
 * A country code in alpha-3, or `null` when it is not one we recognise.
 *
 * `BR` is accepted as legacy input and normalised; `BRA` passes through. A
 * currency code, an empty string, a country we do not know and `null` all
 * answer `null` - the caller must treat that as unproven, never as a pass.
 */
export function normaliseCountryCode(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const code = value.trim().toUpperCase();
  if (code.length === 3) {
    // A currency lands here and is refused: `BRL` is not in the table.
    return KNOWN_ALPHA3.has(code) ? code : null;
  }
  if (code.length === 2) {
    return ALPHA2_TO_ALPHA3[code] ?? null;
  }
  return null;
}

/** Is this the one country the store settles in? Unproven answers false. */
export function isStoreCountry(value: string | null | undefined): boolean {
  return normaliseCountryCode(value) === STORE_COUNTRY_CODE;
}
